import type { Guide, GuideRecordHint } from '../types';

/**
 * S-03.11-01 — prezlecenia z maili i dokumentów (decyzje użytkownika 03.11.2026; scenariusz e2e 44).
 * Dane: mail DEMO 044 z listą dwóch ładunków (dwa prezlecenia), uzupełnienie DEMO 045 przy pierwszym
 * prezleceniu i komplet załączników — PDF, XLSX, XLS, JPG, PNG, wielostronicowy TIFF i zdjęcie HEIC
 * (`e2e/setup/seed-e2e.ts`). Nagrywarka tylko otwiera źródło i przełącza zakładki, niczego nie zapisuje.
 */
const FROM_MAIL: GuideRecordHint = {
  description: 'prezlecenie z maila z załącznikami — z listy prezleceń',
  listPath: '/pre-orders',
};

const MAIL: GuideRecordHint = {
  description: 'mail z listą ładunków — ze skrzynki',
  listPath: '/emails',
};

export const guide: Guide = {
  flagKey: 'preOrderDocuments',
  title: 'Prezlecenia z maili i dokumentów',
  whatItDoes:
    'Prezlecenie można zapisać niekompletne, a system liczy, czego brakuje do zlecenia i do zapisu w TMS — na liście widać „Gotowe do wysłania” albo liczbę braków, na karcie brakujące pola są zaznaczone. Obok danych od razu widzisz dokument, z którego je wpisujesz: mail, PDF, arkusz jako tabelę albo skan i zdjęcie — także HEIC z telefonu — z powiększeniem i obrotem. Jeden mail może dotyczyć kilku prezleceń, a jedno prezlecenie kilku maili — przejdziesz z prezlecenia do maila i z maila do prezlecenia, powiążesz mail albo odepniesz go z powodem. O utworzeniu zlecenia i wysłaniu decyduje człowiek.',
  audience: ['CS', 'Administracja', 'planista FTL', 'Team Leader (planista główny)', 'Trade'],
  presentationItems: ['S-03.11-01'],
  steps: [
    {
      route: '/pre-orders',
      target: 'pre-order-list-documents',
      title: 'Lista prezleceń jak lista dokumentów',
      description:
        'Przy każdym prezleceniu numer, klient, relacja, terminy, stan i komplet pól do zapisu w TMS, liczba maili i plików oraz źródło: z maila, kopia albo wpisane ręcznie. „Gotowe do wysłania” znaczy, że niczego nie brakuje — zlecenie i tak tworzy człowiek.',
      action: 'Otwórz prezlecenie z kolumną „Maile” większą niż jeden.',
      recorder: { account: 'cs' },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'pre-order-send-readiness',
      title: 'Czego brakuje do zlecenia i do zapisu w TMS',
      description:
        'Braki są pogrupowane: do utworzenia zlecenia, dane prezlecenia do pliku, zleceniodawca, ładunek i punkty. Lista liczy się z tych samych reguł, które sprawdzają plik przed zapisem w TMS, więc „gotowe” tu oznacza plik, który przejdzie import.',
      action: 'Sprawdź listę braków.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's44PreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'pre-order-importer-fields',
      title: 'Dane dla importera TMS już na prezleceniu',
      description:
        'Zleceniodawca z adresem, objętość i punkty z oknami czasowymi to pola, których wymaga import w TMS. Uzupełniasz je przed utworzeniem zlecenia; brakujące są zaznaczone. Nieczytelne zostawiasz puste — nie zgadujemy.',
      action: 'Uzupełnij brakujące pola i zapisz.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's44PreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'pre-order-emails',
      title: 'Wszystkie maile prezlecenia',
      description:
        'Mail, z którego powstało prezlecenie, uzupełnienia i korespondencja w sprawie — z rolą, datą i tym, kto powiązał. „Otwórz mail” prowadzi do skrzynki, „Powiąż mail” dopina kolejny, a „Odepnij” wymaga powodu, który zostaje w historii.',
      action: 'Kliknij „Otwórz mail” przy jednym z maili.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's44PreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/show',
      record: FROM_MAIL,
      target: 'source-preview-panel',
      title: 'Dane po lewej, dokument po prawej',
      description:
        'Dokument stoi obok danych od razu — „Zwiń podgląd dokumentu” go chowa, a wybór zostaje zapamiętany dla Twojego konta. Są tu zakładki maili i plików, strzałki do następnego materiału, PDF ze stronami, arkusz jako tabela, a skan i zdjęcie z powiększeniem i obrotem. Wielostronicowy skan przełączasz strona po stronie.',
      action: 'Przejdź do skanu albo zdjęcia z telefonu.',
      recorder: {
        account: 'cs',
        demo: { kind: 'scenario', key: 's44PreOrderId' },
        actions: [{ kind: 'click', target: 'source-preview-show-in-source' }],
      },
    },
    {
      route: '/emails/:emailId/show',
      record: MAIL,
      target: 'email-pre-orders',
      title: 'Z maila do prezleceń',
      description:
        'Na karcie maila widać wszystkie prezlecenia, których dotyczy — mail z listą ładunków ma ich kilka. Przejdziesz do każdego jednym kliknięciem, a brakujące powiążesz z tego miejsca.',
      action: 'Kliknij „Otwórz prezlecenie”.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's44EmailId' } },
    },
  ],
};
